import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { RichTextEditor } from "@/components/crm/RichTextEditor";
import { AiComposer } from "@/components/crm/AiComposer";
import { AttachmentUploader } from "@/components/messagerie/AttachmentUploader";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Tray, PaperPlaneTilt, Archive, LinkBreak, MagnifyingGlass, Envelope, Gear,
  CheckCircle, ChatCircleDots, ArrowClockwise, ArrowUUpLeft, Link as LinkIcon,
  Buildings, Briefcase, UserCircle, IdentificationCard, CaretRight, Circle, WarningCircle,
  Trash, ArrowCounterClockwise, NotePencil, FloppyDisk, File as FileIcon,
  DownloadSimple, X,
} from "@phosphor-icons/react";

const FOLDERS = [
  { key: "reception", label: "Réception", icon: Tray },
  { key: "sent", label: "Envoyés", icon: PaperPlaneTilt },
  { key: "draft", label: "Brouillons", icon: NotePencil },
  { key: "archive", label: "Archive", icon: Archive },
  { key: "unmatched", label: "Non rattachés", icon: LinkBreak },
  { key: "trash", label: "Corbeille", icon: Trash },
];

async function downloadAttachment(fid, filename) {
  try {
    const res = await api.get(`/crm/files/${fid}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename || "document"; document.body.appendChild(a); a.click();
    a.remove(); URL.revokeObjectURL(url);
  } catch { toast.error("Téléchargement impossible."); }
}

const MATCH_LABEL = {
  thread: "Fil de conversation", email: "Contact connu", domain: "Domaine entreprise", manual: "Rattaché manuellement",
};

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}
function ago(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "à l'instant";
  if (s < 3600) return `${Math.floor(s / 60)} min`;
  if (s < 86400) return `${Math.floor(s / 3600)} h`;
  if (s < 172800) return "hier";
  return `${Math.floor(s / 86400)} j`;
}

export default function Messagerie() {
  const [folder, setFolder] = useState("reception");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [unread, setUnread] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [conv, setConv] = useState(null);
  const [loadingConv, setLoadingConv] = useState(false);
  const [account, setAccount] = useState(undefined);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [replyAtts, setReplyAtts] = useState([]);
  const [replyDraftId, setReplyDraftId] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [composeInit, setComposeInit] = useState(null); // null = fermé, objet = ouvert

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const { data } = await api.get("/crm/inbox/conversations", { params: { folder, q: search } });
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setLoadingList(false); }
  }, [folder, search]);

  const loadUnread = useCallback(async () => {
    try { const { data } = await api.get("/crm/inbox/unread-count"); setUnread(data.unread || 0); }
    catch { /* noop */ }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadUnread(); }, [loadUnread]);
  useEffect(() => { api.get("/crm/email-account").then((r) => setAccount(r.data)).catch(() => setAccount(null)); }, []);

  const openConv = useCallback(async (id) => {
    setSelectedId(id);
    setConv(null);
    setReply("");
    setReplyAtts([]);
    setReplyDraftId(null);
    setLoadingConv(true);
    try {
      const { data } = await api.get(`/crm/inbox/conversation/${encodeURIComponent(id)}`);
      setConv(data);
      // opening a matched conversation marks its inbound messages read
      setItems((arr) => arr.map((it) => (it.id === id ? { ...it, unread: 0 } : it)));
      loadUnread();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setLoadingConv(false); }
  }, [loadUnread]);

  const openItem = useCallback(async (it) => {
    if (it.type !== "draft") return openConv(it.id);
    try {
      const { data } = await api.get(`/crm/inbox/conversation/${encodeURIComponent(it.id)}`);
      const draftId = it.id.slice(3);
      if (data.reply_to_conv_id) {
        await openConv(data.reply_to_conv_id);
        setReply(data.body_html || "");
        setReplyAtts(data.attachments || []);
        setReplyDraftId(draftId);
      } else {
        setComposeInit({ draftId, to_email: data.to_email, to_name: data.to_name,
          subject: data.subject, body_html: data.body_html, atts: data.attachments || [] });
      }
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  }, [openConv]);

  const sendReply = async () => {
    const html = reply.trim();
    if (!html) { toast.error("Le message est vide."); return; }
    setSending(true);
    try {
      await api.post(`/crm/inbox/conversation/${encodeURIComponent(selectedId)}/reply`, {
        body_html: html, public_base: process.env.REACT_APP_BACKEND_URL,
        extra_file_ids: replyAtts.map((a) => a.file_id), draft_id: replyDraftId || undefined,
      });
      toast.success("Réponse envoyée");
      setReply(""); setReplyAtts([]); setReplyDraftId(null);
      await openConv(selectedId);
      loadList();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const saveReplyDraft = async () => {
    setSavingDraft(true);
    try {
      const { data } = await api.post("/crm/inbox/drafts", {
        id: replyDraftId || undefined, reply_to_conv_id: selectedId,
        to_email: conv?.to_email, subject: conv?.subject,
        body_html: reply, extra_file_ids: replyAtts.map((a) => a.file_id),
      });
      setReplyDraftId(data.id);
      toast.success("Brouillon enregistré");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setSavingDraft(false); }
  };

  const trashConv = async () => {
    try {
      await api.post(`/crm/inbox/conversation/${encodeURIComponent(selectedId)}/trash`);
      toast.success("Déplacé dans la corbeille");
      setSelectedId(null); setConv(null); loadList(); loadUnread();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const restoreConv = async () => {
    try {
      await api.post(`/crm/inbox/conversation/${encodeURIComponent(selectedId)}/restore`);
      toast.success("Restauré");
      setSelectedId(null); setConv(null); loadList();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const deleteForever = async () => {
    try {
      await api.delete(`/crm/inbox/conversation/${encodeURIComponent(selectedId)}`);
      toast.success("Supprimé définitivement");
      setSelectedId(null); setConv(null); loadList();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const archive = async () => {
    const un = conv?.archived;
    try {
      await api.post(`/crm/inbox/conversation/${encodeURIComponent(selectedId)}/${un ? "unarchive" : "archive"}`);
      toast.success(un ? "Conversation désarchivée" : "Conversation archivée");
      setSelectedId(null); setConv(null); loadList();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const onLinked = () => { setLinkOpen(false); toast.success("Email rattaché au CRM"); setSelectedId(null); setConv(null); loadList(); };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/crm/email-account/sync-now");
      if (data.last_inbox_error) {
        toast.error(`Réception impossible : ${data.last_inbox_error}. Vérifiez le serveur IMAP dans Réglages → Email.`);
      } else if (data.new_replies > 0) {
        toast.success(`${data.new_replies} nouveau(x) message(s) rattaché(s) reçu(s).`);
      } else {
        toast.success("Boîte à jour — aucune nouvelle réponse rattachée.");
      }
      loadList(); loadUnread();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setSyncing(false); }
  };

  return (
    <div data-testid="messagerie-page">
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div>
          <div className="overline mb-2">Messagerie commerciale</div>
          <h1 className="font-display text-3xl font-bold">Boîte de réception interKO</h1>
          <p className="text-muted text-sm mt-1">Vos conversations commerciales, rattachées automatiquement à vos opportunités.</p>
        </div>
        <div className="shrink-0">
          {account === undefined ? null : account ? (
            <div className="flex items-center gap-2">
              <button onClick={syncNow} disabled={syncing} data-testid="messagerie-sync-btn"
                className="flex items-center gap-1.5 text-xs font-semibold border border-border rounded-full px-3 py-1.5 text-muted hover:text-fg hover:bg-surface-2 disabled:opacity-50">
                <ArrowClockwise size={13} className={syncing ? "animate-spin" : ""} /> {syncing ? "Synchro…" : "Synchroniser"}
              </button>
              <div className="flex items-center gap-2 text-xs font-mono text-accent border border-accent/30 rounded-full px-3 py-1.5" data-testid="messagerie-account">
                <CheckCircle size={13} weight="fill" /> {account.email}
                <Link to="/app/settings/email" className="text-muted hover:text-fg ml-1"><Gear size={13} /></Link>
              </div>
            </div>
          ) : (
            <Link to="/app/settings/email" data-testid="messagerie-connect-email"
              className="flex items-center gap-2 text-sm bg-accent text-black font-semibold px-4 py-2 rounded-md hover-lift">
              <Envelope size={15} /> Connecter ma boîte email
            </Link>
          )}
        </div>
      </div>

      <div className="flex gap-3 h-[calc(100vh-13rem)] min-h-[520px]">
        {/* ---------------- Column 1 : folders + conversation list ---------------- */}
        <div className="w-[300px] xl:w-[340px] shrink-0 flex flex-col surface rounded-lg overflow-hidden">
          <div className="p-2.5 border-b border-border">
            <button onClick={() => setComposeInit({})} data-testid="compose-btn"
              className="w-full flex items-center justify-center gap-2 bg-accent text-black font-semibold text-sm py-2.5 rounded-md hover-lift">
              <NotePencil size={16} weight="bold" /> Écrire un message
            </button>
          </div>
          <div className="px-2.5 pt-2.5 pb-2 border-b border-border">
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="messagerie-search"
                placeholder="Rechercher…" className="bg-surface-2 border-border pl-8 h-9 text-sm" />
            </div>
          </div>
          <div className="p-2 border-b border-border space-y-0.5" data-testid="folder-list">
            {FOLDERS.map((f) => {
              const Icon = f.icon;
              const active = folder === f.key;
              return (
                <button key={f.key} data-testid={`folder-tab-${f.key}`}
                  onClick={() => { setFolder(f.key); setSelectedId(null); setConv(null); }}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${active ? "bg-accent/15 text-accent" : "text-muted hover:text-fg hover:bg-surface-2"}`}>
                  <span className="flex items-center gap-2.5 truncate"><Icon size={17} weight={active ? "fill" : "regular"} /> {f.label}</span>
                  {f.key === "reception" && unread > 0 && (
                    <span data-testid="reception-unread-badge" className="shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-accent text-black text-[10px] font-bold font-mono flex items-center justify-center">{unread}</span>
                  )}
                </button>
              );
            })}
          </div>
          <div className="flex-1 overflow-y-auto" data-testid="conversation-list">
            {loadingList ? (
              <div className="p-4 text-muted text-sm animate-pulse">Chargement…</div>
            ) : items.length === 0 ? (
              <EmptyList folder={folder} />
            ) : items.map((it) => (
              <ConversationRow key={it.id} it={it} active={selectedId === it.id} onClick={() => openItem(it)} />
            ))}
          </div>
        </div>

        {/* ---------------- Column 2 : thread ---------------- */}
        <div className="flex-1 min-w-0 flex flex-col surface rounded-lg overflow-hidden">
          {!selectedId ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-8" data-testid="thread-empty">
              <ChatCircleDots size={44} weight="duotone" className="text-accent mb-3" />
              <div className="font-display font-bold text-fg mb-1">Sélectionnez une conversation</div>
              <p className="text-muted text-sm max-w-xs">Choisissez un échange à gauche pour lire le fil et répondre directement depuis interKO.</p>
            </div>
          ) : loadingConv ? (
            <div className="p-6 text-muted text-sm animate-pulse">Chargement de la conversation…</div>
          ) : conv ? (
            <ThreadView conv={conv} convId={selectedId} account={account} reply={reply} setReply={setReply} sending={sending}
              onSend={sendReply} onArchive={archive} onOpenLink={() => setLinkOpen(true)}
              inTrash={folder === "trash"} onTrash={trashConv} onRestore={restoreConv} onDelete={deleteForever}
              replyAtts={replyAtts} setReplyAtts={setReplyAtts} onSaveDraft={saveReplyDraft}
              savingDraft={savingDraft} hasDraft={!!replyDraftId} />
          ) : null}
        </div>

        {/* ---------------- Column 3 : CRM context ---------------- */}
        <div className="w-[290px] shrink-0 hidden xl:flex flex-col surface rounded-lg overflow-hidden" data-testid="crm-context">
          <CrmContext conv={conv} />
        </div>
      </div>

      {linkOpen && conv?.type === "unmatched" && (
        <LinkDialog conv={conv} onClose={() => setLinkOpen(false)} onLinked={onLinked} />
      )}

      {composeInit && (
        <ComposeDialog init={composeInit} account={account}
          onClose={() => setComposeInit(null)}
          onDone={() => { setComposeInit(null); loadList(); }} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
function ConversationRow({ it, active, onClick }) {
  const idSafe = String(it.id).replace(/[^a-zA-Z0-9]/g, "_");
  return (
    <button onClick={onClick} data-testid={`conversation-item-${idSafe}`}
      className={`w-full text-left px-3 py-3 border-b border-border/50 transition-colors ${active ? "bg-accent/10" : "hover:bg-surface-2/50"}`}>
      <div className="flex items-center justify-between gap-2 mb-0.5">
        <span className={`text-sm truncate ${it.unread ? "font-bold text-fg" : "font-medium text-fg/90"}`}>{it.contact_name || "—"}</span>
        <span className="text-[10px] text-muted font-mono shrink-0">{ago(it.last_at)}</span>
      </div>
      {it.company_name && <div className="text-xs text-accent/90 truncate flex items-center gap-1"><Buildings size={11} /> {it.company_name}</div>}
      <div className={`text-xs truncate mt-0.5 ${it.unread ? "text-fg" : "text-muted"}`}>{it.subject}</div>
      {it.snippet && <div className="text-[11px] text-muted/80 truncate mt-0.5">{it.snippet}</div>}
      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
        {it.unread > 0 && <span className="shrink-0 flex items-center gap-1 text-[10px] font-mono text-accent"><Circle size={7} weight="fill" /> {it.unread} nouveau{it.unread > 1 ? "x" : ""}</span>}
        {it.is_draft && <span className="text-[10px] font-mono text-accent border border-accent/40 bg-accent/10 rounded-full px-1.5 py-0.5 flex items-center gap-1"><NotePencil size={9} /> Brouillon</span>}
        {it.replied && <span className="text-[10px] font-mono text-[#22C55E] border border-[#22C55E]/40 bg-[#22C55E]/10 rounded-full px-1.5 py-0.5 flex items-center gap-1"><ChatCircleDots size={9} weight="fill" /> Réponse</span>}
        {it.type === "unmatched" && !it.in_trash && <span className="text-[10px] font-mono text-[#F59E0B] border border-[#F59E0B]/40 bg-[#F59E0B]/10 rounded-full px-1.5 py-0.5 flex items-center gap-1"><LinkBreak size={9} /> Non rattaché</span>}
        {it.opportunity_title && <span className="text-[10px] font-mono text-muted truncate max-w-[130px] flex items-center gap-1"><Briefcase size={9} /> {it.opportunity_title}</span>}
      </div>
    </button>
  );
}

function EmptyList({ folder }) {
  const msg = {
    reception: "Aucune réponse reçue pour l'instant. Les réponses à vos emails de prospection apparaîtront ici.",
    sent: "Aucun email envoyé. Contactez un décideur depuis une opportunité pour démarrer.",
    draft: "Aucun brouillon. Cliquez « Écrire un message » ou enregistrez une réponse en cours.",
    archive: "Aucune conversation archivée.",
    unmatched: "Aucun email non rattaché. Les emails sans lien CRM connu atterrissent ici, sans notification.",
    trash: "La corbeille est vide.",
  }[folder];
  return (
    <div className="p-6 text-center text-muted text-xs" data-testid="conversation-list-empty">
      <Tray size={30} weight="duotone" className="mx-auto mb-2 text-muted/50" />
      {msg}
    </div>
  );
}

/* ------------------------------------------------------------------ */
function ThreadView({ conv, convId, account, reply, setReply, sending, onSend, onArchive, onOpenLink,
                     inTrash, onTrash, onRestore, onDelete, replyAtts, setReplyAtts, onSaveDraft, savingDraft, hasDraft }) {
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [conv]);
  const isThread = conv.type === "thread";

  return (
    <>
      <div className="px-4 py-3 border-b border-border flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-display font-bold text-fg truncate">{conv.subject || "(sans objet)"}</div>
          <div className="text-xs text-muted mt-0.5">{isThread ? `${conv.messages?.length || 0} message(s)` : conv.from_email}</div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {inTrash ? (
            <>
              <button onClick={onRestore} data-testid="conversation-restore-btn"
                className="flex items-center gap-1.5 text-xs text-muted hover:text-fg border border-border rounded-md px-3 py-1.5 hover:bg-surface-2">
                <ArrowCounterClockwise size={13} /> Restaurer
              </button>
              <button onClick={onDelete} data-testid="conversation-delete-btn"
                className="flex items-center gap-1.5 text-xs text-destructive border border-destructive/40 rounded-md px-3 py-1.5 hover:bg-destructive/10">
                <Trash size={13} /> Supprimer définitivement
              </button>
            </>
          ) : conv.type === "unmatched" ? (
            <>
              <button onClick={onOpenLink} data-testid="link-message-btn"
                className="flex items-center gap-1.5 text-xs bg-accent text-black font-semibold px-3 py-1.5 rounded-md hover-lift">
                <LinkIcon size={13} /> Rattacher au CRM
              </button>
              <button onClick={onTrash} data-testid="conversation-trash-btn"
                className="flex items-center gap-1.5 text-xs text-muted hover:text-destructive border border-border rounded-md px-2.5 py-1.5 hover:bg-surface-2">
                <Trash size={13} />
              </button>
            </>
          ) : (
            <>
              <button onClick={onArchive} data-testid="conversation-archive-btn"
                className="flex items-center gap-1.5 text-xs text-muted hover:text-fg border border-border rounded-md px-3 py-1.5 hover:bg-surface-2">
                <Archive size={13} /> {conv.archived ? "Désarchiver" : "Archiver"}
              </button>
              <button onClick={onTrash} data-testid="conversation-trash-btn"
                className="flex items-center gap-1.5 text-xs text-muted hover:text-destructive border border-border rounded-md px-2.5 py-1.5 hover:bg-surface-2">
                <Trash size={13} />
              </button>
            </>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {(conv.messages || []).map((m) => <MessageBubble key={m.id} m={m} />)}
        <div ref={endRef} />
      </div>

      {isThread && !inTrash && (
        <div className="border-t border-border p-3">
          {account ? (
            <>
              <div className="mb-2">
                <AiComposer
                  run={async (payload) => {
                    const { data } = await api.post("/crm/inbox/assist", { conv_id: convId, ...payload });
                    return data;
                  }}
                  getCurrent={() => ({ body_html: reply })}
                  onApply={(v) => setReply(v.body_html || "")}
                  defaultVariants={2}
                  testid="reply-ai"
                />
              </div>
              <RichTextEditor value={reply} onChange={setReply} testid="reply-editor" minHeight={90}
                placeholder="Répondre depuis interKO…" />
              <div className="mt-2"><AttachmentUploader value={replyAtts} onChange={setReplyAtts} testid="reply-attachments" compact /></div>
              <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
                <span className="text-[11px] text-muted font-mono">Envoyé depuis {account.email}</span>
                <div className="flex items-center gap-2">
                  <button onClick={onSaveDraft} disabled={savingDraft} data-testid="reply-save-draft-btn"
                    className="flex items-center gap-1.5 text-xs text-muted hover:text-fg border border-border rounded-md px-3 py-2 hover:bg-surface-2 disabled:opacity-50">
                    <FloppyDisk size={14} /> {savingDraft ? "…" : (hasDraft ? "Brouillon mis à jour" : "Enregistrer le brouillon")}
                  </button>
                  <button onClick={onSend} disabled={sending} data-testid="reply-send-btn"
                    className="flex items-center gap-1.5 text-sm bg-accent text-black font-semibold px-4 py-2 rounded-md hover-lift disabled:opacity-50">
                    <ArrowUUpLeft size={15} weight="fill" /> {sending ? "Envoi…" : "Répondre"}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-2 text-xs text-[#F59E0B]" data-testid="reply-no-account">
              <WarningCircle size={15} /> Connectez votre boîte email pour répondre.
              <Link to="/app/settings/email" className="text-accent hover:underline">Réglages → Email</Link>
            </div>
          )}
        </div>
      )}
    </>
  );
}

function MessageBubble({ m }) {
  const out = m.direction === "out";
  return (
    <div data-testid={`thread-message-${m.id}`}
      className={`rounded-md border px-3.5 py-3 ${out ? "border-l-2 border-l-accent bg-accent/[0.04] border-border" : "border-l-2 border-l-[#22C55E] bg-[#22C55E]/[0.04] border-border"}`}>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded ${out ? "text-accent bg-accent/10" : "text-[#22C55E] bg-[#22C55E]/10"}`}>{out ? "Vous" : "Reçu"}</span>
          <span className="text-xs text-fg/90 truncate">{m.from_name || m.from_email}</span>
          {m.is_followup && <span className="text-[10px] font-mono text-muted flex items-center gap-0.5"><ArrowClockwise size={10} /> relance</span>}
          {m.is_auto_reply && <span className="text-[10px] font-mono text-muted">auto</span>}
        </div>
        <span className="text-[10px] text-muted font-mono shrink-0">{fmt(m.at)}</span>
      </div>
      {m.body_html
        ? <div className="text-sm text-fg/90 prose-editor break-words" dangerouslySetInnerHTML={{ __html: m.body_html }} />
        : <div className="text-sm text-fg/85 whitespace-pre-line break-words">{m.body_text}</div>}
      {(m.attachments || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2.5 pt-2 border-t border-border/50" data-testid={`message-attachments-${m.id}`}>
          {m.attachments.map((a, i) => (
            <button key={a.file_id || i} onClick={() => a.file_id && downloadAttachment(a.file_id, a.filename)}
              disabled={!a.file_id} data-testid={`attachment-${a.file_id || i}`}
              className="flex items-center gap-1.5 text-[11px] bg-surface-2 border border-border rounded-full px-2.5 py-1 hover:border-accent/50 hover:text-accent transition-colors disabled:opacity-60 disabled:cursor-default max-w-[240px]">
              <FileIcon size={12} className="shrink-0" />
              <span className="truncate">{a.filename || "document"}</span>
              {a.file_id && <DownloadSimple size={12} className="shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
function CrmContext({ conv }) {
  if (!conv) {
    return <div className="flex-1 flex items-center justify-center text-center px-6 text-muted text-xs" data-testid="crm-context-empty">
      <span><IdentificationCard size={30} weight="duotone" className="mx-auto mb-2 text-muted/50" />Le contexte CRM de la conversation s'affichera ici.</span>
    </div>;
  }
  const ctx = conv.crm_context || {};
  const empty = !ctx.company && !ctx.contact && !ctx.opportunity && !ctx.consultant;
  return (
    <>
      <div className="px-4 py-3 border-b border-border">
        <div className="overline">Contexte CRM</div>
        {conv.matched === false && <div className="text-[11px] text-[#F59E0B] mt-1 flex items-center gap-1"><LinkBreak size={12} /> Email non rattaché</div>}
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {empty ? (
          <div className="text-muted text-xs text-center py-6">Aucun élément CRM lié à cet email pour le moment.</div>
        ) : (
          <>
            {ctx.opportunity && (
              <CtxCard icon={Briefcase} label="Opportunité" testid="crm-context-opportunity"
                to={`/app/opportunity/${ctx.opportunity.id}`}>
                <div className="text-sm text-fg font-medium">{ctx.opportunity.role_title}</div>
                {ctx.opportunity.posted_at && <div className="text-[11px] text-muted font-mono">{fmt(ctx.opportunity.posted_at)}</div>}
              </CtxCard>
            )}
            {ctx.company && (
              <CtxCard icon={Buildings} label="Entreprise" testid="crm-context-company"
                to={`/app/company/${ctx.company.id}`}>
                <div className="text-sm text-fg font-medium">{ctx.company.name}</div>
                <div className="text-[11px] text-muted">{[ctx.company.sector, ctx.company.city].filter(Boolean).join(" · ")}</div>
                {ctx.company.domain && <div className="text-[11px] text-accent/80 font-mono truncate">{ctx.company.domain}</div>}
              </CtxCard>
            )}
            {ctx.contact && (
              <CtxCard icon={UserCircle} label="Contact" testid="crm-context-contact">
                <div className="text-sm text-fg font-medium">{ctx.contact.name}</div>
                {ctx.contact.title && <div className="text-[11px] text-muted">{ctx.contact.title}</div>}
                {ctx.contact.email && <div className="text-[11px] text-accent/80 font-mono truncate">{ctx.contact.email}</div>}
              </CtxCard>
            )}
            {ctx.consultant && (
              <CtxCard icon={IdentificationCard} label="Consultant positionné" testid="crm-context-consultant">
                <div className="text-sm text-fg font-medium">{ctx.consultant.name}</div>
                {ctx.consultant.title && <div className="text-[11px] text-muted">{ctx.consultant.title}</div>}
              </CtxCard>
            )}
          </>
        )}
      </div>
    </>
  );
}

function CtxCard({ icon: Icon, label, children, to, testid }) {
  const inner = (
    <div className="bg-surface-2 border border-border rounded-md px-3 py-2.5 hover:border-accent/40 transition-colors" data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-mono uppercase tracking-wider text-muted flex items-center gap-1"><Icon size={12} /> {label}</span>
        {to && <CaretRight size={12} className="text-muted" />}
      </div>
      {children}
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

/* ------------------------------------------------------------------ */
function LinkDialog({ conv, onClose, onLinked }) {
  const msgId = conv.messages?.[0]?.id;
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/crm/inbox/contacts/search", { params: { q } }); setResults(data.items || []); }
      catch { setResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const link = async (payload) => {
    setBusy(true);
    try { await api.post(`/crm/inbox/messages/${msgId}/link`, payload); onLinked(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-surface border-border max-w-md" data-testid="link-dialog">
        <DialogHeader><DialogTitle className="font-display">Rattacher cet email</DialogTitle></DialogHeader>
        <DialogDescription className="text-muted text-sm">Reliez « {conv.from_email} » à un contact existant de votre CRM.</DialogDescription>
        <div className="relative mt-1">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="contact-search-input" autoFocus
            placeholder="Rechercher un contact (nom, email)…" className="bg-surface-2 border-border pl-8" />
        </div>
        <div className="max-h-56 overflow-y-auto space-y-1">
          {results.map((c) => (
            <button key={c.id} disabled={busy} data-testid={`contact-result-${c.id}`}
              onClick={() => link({ contact_id: c.id, company_id: undefined })}
              className="w-full text-left px-3 py-2 rounded-md hover:bg-surface-2 border border-transparent hover:border-accent/30 transition-colors">
              <div className="text-sm text-fg">{c.name}</div>
              <div className="text-[11px] text-muted font-mono truncate">{c.email}{c.company ? ` · ${c.company}` : ""}</div>
            </button>
          ))}
          {q.trim().length >= 2 && results.length === 0 && (
            <div className="text-xs text-muted px-3 py-3">Aucun contact trouvé.</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
function ComposeDialog({ init, account, onClose, onDone }) {
  const [toEmail, setToEmail] = useState(init.to_email || "");
  const [toName, setToName] = useState(init.to_name || "");
  const [subject, setSubject] = useState(init.subject || "");
  const [body, setBody] = useState(init.body_html || "");
  const [atts, setAtts] = useState(init.atts || []);
  const [contactId, setContactId] = useState(init.contact_id || null);
  const [companyId, setCompanyId] = useState(init.company_id || null);
  const [linkedName, setLinkedName] = useState(init.to_name && init.contact_id ? init.to_name : "");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const draftId = init.draftId || null;

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/crm/inbox/contacts/search", { params: { q } }); setResults(data.items || []); }
      catch { setResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const pickContact = (c) => {
    setToEmail(c.email || ""); setToName(c.name || ""); setContactId(c.id);
    setLinkedName(c.name); setQ(""); setResults([]);
  };
  const clearLink = () => { setContactId(null); setCompanyId(null); setLinkedName(""); };

  const payload = () => ({
    to_email: toEmail.trim(), to_name: toName.trim(), subject: subject.trim(),
    body_html: body, extra_file_ids: atts.map((a) => a.file_id),
    contact_id: contactId, company_id: companyId,
  });

  const send = async () => {
    if (!account) { toast.error("Connectez d'abord votre boîte email."); return; }
    if (!toEmail.trim() || !toEmail.includes("@")) { toast.error("Destinataire invalide."); return; }
    if (!subject.trim()) { toast.error("L'objet est requis."); return; }
    if (!body.trim()) { toast.error("Le message est vide."); return; }
    setSending(true);
    try {
      await api.post("/crm/inbox/compose", { ...payload(), draft_id: draftId || undefined, public_base: process.env.REACT_APP_BACKEND_URL });
      toast.success("Message envoyé");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      await api.post("/crm/inbox/drafts", { id: draftId || undefined, ...payload() });
      toast.success("Brouillon enregistré");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-surface border-border max-w-xl" data-testid="compose-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><NotePencil size={18} className="text-accent" /> Nouveau message</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Composer un nouveau message</DialogDescription>

        {!account && (
          <div className="flex items-center gap-2 text-xs text-[#F59E0B] bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-md px-3 py-2" data-testid="compose-no-account">
            <WarningCircle size={15} /> Connectez votre boîte email pour envoyer.
            <Link to="/app/settings/email" className="text-accent hover:underline">Réglages → Email</Link>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="overline block mb-1">Destinataire</label>
            <Input value={toEmail} onChange={(e) => setToEmail(e.target.value)} data-testid="compose-to"
              placeholder="client@entreprise.fr" className="bg-surface-2 border-border" />
          </div>

          {linkedName ? (
            <div className="flex items-center gap-2 text-xs" data-testid="compose-linked">
              <span className="flex items-center gap-1.5 text-accent bg-accent/10 border border-accent/30 rounded-full px-2.5 py-1">
                <UserCircle size={13} /> Lié à {linkedName}
                <button onClick={clearLink} data-testid="compose-clear-link" className="ml-1 text-muted hover:text-destructive"><X size={11} weight="bold" /></button>
              </span>
            </div>
          ) : (
            <div className="relative">
              <label className="overline block mb-1">Lier à un contact CRM (optionnel)</label>
              <div className="relative">
                <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="compose-contact-search"
                  placeholder="Rechercher un contact…" className="bg-surface-2 border-border pl-8" />
              </div>
              {results.length > 0 && (
                <div className="mt-1 max-h-40 overflow-y-auto border border-border rounded-md bg-surface-2" data-testid="compose-contact-results">
                  {results.map((c) => (
                    <button key={c.id} onClick={() => pickContact(c)} data-testid={`compose-contact-${c.id}`}
                      className="w-full text-left px-3 py-2 hover:bg-accent/10 transition-colors">
                      <div className="text-sm text-fg">{c.name}</div>
                      <div className="text-[11px] text-muted font-mono truncate">{c.email}{c.company ? ` · ${c.company}` : ""}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div>
            <label className="overline block mb-1">Objet</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="compose-subject"
              placeholder="Objet du message" className="bg-surface-2 border-border" />
          </div>

          <div>
            <label className="overline block mb-1">Message</label>
            <div className="mb-2">
              <AiComposer
                run={async (p) => {
                  const { data } = await api.post("/crm/inbox/assist", { contact_id: contactId, company_id: companyId, ...p });
                  return data;
                }}
                getCurrent={() => ({ subject, body_html: body })}
                onApply={(v) => { if (v.subject) setSubject(v.subject); setBody(v.body_html || ""); }}
                defaultVariants={2}
                testid="compose-ai"
              />
            </div>
            <RichTextEditor value={body} onChange={setBody} testid="compose-editor" minHeight={140}
              placeholder="Écrivez votre message…" />
          </div>

          <AttachmentUploader value={atts} onChange={setAtts} testid="compose-attachments" />
        </div>

        <div className="flex items-center justify-between gap-2 pt-2 border-t border-border mt-1">
          <button onClick={saveDraft} disabled={saving} data-testid="compose-save-draft"
            className="flex items-center gap-1.5 text-xs text-muted hover:text-fg border border-border rounded-md px-3 py-2 hover:bg-surface-2 disabled:opacity-50">
            <FloppyDisk size={14} /> {saving ? "…" : "Enregistrer le brouillon"}
          </button>
          <button onClick={send} disabled={sending || !account} data-testid="compose-send"
            className="flex items-center gap-1.5 text-sm bg-accent text-black font-semibold px-5 py-2 rounded-md hover-lift disabled:opacity-50">
            <PaperPlaneTilt size={15} weight="fill" /> {sending ? "Envoi…" : "Envoyer"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
